import {PosterOpType} from '@PosterWhiteboard/collaboration/poster-op.types';
import type {ItemAddOp, ItemRemoveOp, ItemSetOp, ItemsOrderSetOp, PageAddOp, PageOrderSetOp, PageRemoveOp, PageSetOp, PosterOp, SnapshotRecord} from '@PosterWhiteboard/collaboration/poster-op.types';
import {applyRecordChanges} from '@PosterWhiteboard/collaboration/record-changes.library';
import {
  getItem,
  getItemsById,
  getItemsOrder,
  getPage,
  getPageOrder,
  getPageProps,
  getPagesById,
  getPosterProps,
  replaceItems,
  replacePageProps,
  replacePages,
  replacePosterProps,
} from '@PosterWhiteboard/collaboration/poster-snapshot.library';
import {insertIdAfter, reconcileRequestedOrder} from '@PosterWhiteboard/collaboration/id-order.library';
import {assertNever} from '@Utils/assert-never.util';

type WithinPageOp = PageSetOp | ItemAddOp | ItemRemoveOp | ItemSetOp | ItemsOrderSetOp;

/**
 * Folds ops into a poster snapshot and returns the result; `snapshot` is never mutated.
 *
 * Ops are written against a poster that other people may have changed since, so applying is
 * tolerant: an op whose page or item no longer exists is dropped (delete wins over edit), and the
 * last page can never be removed. Every replica that folds the same ops in the same order reaches
 * the same snapshot.
 *
 * One exception to "delete wins": a set of an item that the same `ops` only adds later is held and
 * applied right after that add. A tab that re-created an uploaded image through undo or redo used to
 * send the file's set ahead of the add; dropping it stored the image without its file for good.
 */
export const applyPosterOps = (snapshot: SnapshotRecord, ops: PosterOp[]): SnapshotRecord => {
  const lastAddIndexByItemId = new Map<string, number>();
  ops.forEach((op, index): void => {
    if (op.type === PosterOpType.ITEM_ADD) {
      lastAddIndexByItemId.set(op.itemId, index);
    }
  });
  const setsHeldForAdd = new Map<string, ItemSetOp[]>();
  return ops.reduce((current, op, index): SnapshotRecord => {
    if (op.type === PosterOpType.ITEM_SET && isSetAheadOfItsAdd(current, op, index, lastAddIndexByItemId)) {
      setsHeldForAdd.set(op.itemId, [...(setsHeldForAdd.get(op.itemId) ?? []), op]);
      return current;
    }
    const applied = applyPosterOp(current, op);
    if (op.type !== PosterOpType.ITEM_ADD) {
      return applied;
    }
    const heldSets = setsHeldForAdd.get(op.itemId) ?? [];
    setsHeldForAdd.delete(op.itemId);
    return heldSets.reduce(applyPosterOp, applied);
  }, snapshot);
};

const isSetAheadOfItsAdd = (snapshot: SnapshotRecord, op: ItemSetOp, index: number, lastAddIndexByItemId: Map<string, number>): boolean => {
  const addIndex = lastAddIndexByItemId.get(op.itemId);
  if (addIndex === undefined || addIndex < index) {
    return false;
  }
  const page = getPage(snapshot, op.pageId);
  return page === null || getItem(page, op.itemId) === null;
};

const applyPosterOp = (snapshot: SnapshotRecord, op: PosterOp): SnapshotRecord => {
  switch (op.type) {
    case PosterOpType.POSTER_SET:
      return replacePosterProps(snapshot, applyRecordChanges(getPosterProps(snapshot), op.changes));
    case PosterOpType.PAGE_ADD:
      return addPage(snapshot, op);
    case PosterOpType.PAGE_REMOVE:
      return removePage(snapshot, op);
    case PosterOpType.PAGE_ORDER_SET:
      return setPageOrder(snapshot, op);
    case PosterOpType.PAGE_SET:
    case PosterOpType.ITEM_ADD:
    case PosterOpType.ITEM_REMOVE:
    case PosterOpType.ITEM_SET:
    case PosterOpType.ITEMS_ORDER_SET:
      return applyOpWithinPage(snapshot, op);
    default:
      return assertNever(op);
  }
};

const addPage = (snapshot: SnapshotRecord, op: PageAddOp): SnapshotRecord => {
  return replacePages(snapshot, {...getPagesById(snapshot), [op.pageId]: op.page}, insertIdAfter(getPageOrder(snapshot), op.pageId, op.afterPageId));
};

const removePage = (snapshot: SnapshotRecord, op: PageRemoveOp): SnapshotRecord => {
  const pagesById = getPagesById(snapshot);
  const {[op.pageId]: _removedPage, ...remainingPagesById} = pagesById;
  if (!Object.hasOwn(pagesById, op.pageId) || Object.keys(remainingPagesById).length === 0) {
    return snapshot;
  }
  return replacePages(
    snapshot,
    remainingPagesById,
    getPageOrder(snapshot).filter((pageId) => pageId !== op.pageId)
  );
};

const setPageOrder = (snapshot: SnapshotRecord, op: PageOrderSetOp): SnapshotRecord => {
  const pagesById = getPagesById(snapshot);
  return replacePages(snapshot, pagesById, reconcileRequestedOrder(op.pageOrder, getPageOrder(snapshot), new Set(Object.keys(pagesById))));
};

const applyOpWithinPage = (snapshot: SnapshotRecord, op: WithinPageOp): SnapshotRecord => {
  const page = getPage(snapshot, op.pageId);
  if (!page) {
    return snapshot;
  }
  const updatedPage = applyOpToPage(page, op);
  return updatedPage === page ? snapshot : replacePages(snapshot, {...getPagesById(snapshot), [op.pageId]: updatedPage}, getPageOrder(snapshot));
};

const applyOpToPage = (page: SnapshotRecord, op: WithinPageOp): SnapshotRecord => {
  switch (op.type) {
    case PosterOpType.PAGE_SET:
      return replacePageProps(page, applyRecordChanges(getPageProps(page), op.changes));
    case PosterOpType.ITEM_ADD:
      return replaceItems(page, {...getItemsById(page), [op.itemId]: op.item}, insertIdAfter(getItemsOrder(page), op.itemId, op.afterItemId));
    case PosterOpType.ITEM_REMOVE:
      return removeItem(page, op);
    case PosterOpType.ITEM_SET:
      return setItemProps(page, op);
    case PosterOpType.ITEMS_ORDER_SET:
      return setItemsOrder(page, op);
    default:
      return assertNever(op);
  }
};

const removeItem = (page: SnapshotRecord, op: ItemRemoveOp): SnapshotRecord => {
  const itemsById = getItemsById(page);
  const {[op.itemId]: _removedItem, ...remainingItemsById} = itemsById;
  if (!Object.hasOwn(itemsById, op.itemId)) {
    return page;
  }
  return replaceItems(
    page,
    remainingItemsById,
    getItemsOrder(page).filter((itemId) => itemId !== op.itemId)
  );
};

const setItemProps = (page: SnapshotRecord, op: ItemSetOp): SnapshotRecord => {
  const item = getItem(page, op.itemId);
  if (!item) {
    return page;
  }
  return replaceItems(page, {...getItemsById(page), [op.itemId]: applyRecordChanges(item, op.changes)}, getItemsOrder(page));
};

const setItemsOrder = (page: SnapshotRecord, op: ItemsOrderSetOp): SnapshotRecord => {
  const itemsById = getItemsById(page);
  return replaceItems(page, itemsById, reconcileRequestedOrder(op.itemsOrder, getItemsOrder(page), new Set(Object.keys(itemsById))));
};
